import { useState, useEffect, useCallback } from 'react'
import { useAuthFetch } from '../lib/useAuthFetch'

const API = import.meta.env.VITE_API_URL ?? ''

// One nearby rival, scored by how much of YOUR catalog it also carries.
export interface RivalDensityRow {
  competitor_id: string
  name: string
  business_type: string | null // 'storefront' | 'delivery'
  distance_mi: number | null
  brand_overlap: number // 0..1 — share of your brands this rival carries
  sku_overlap: number // 0..1 — share of your SKUs this rival carries
  shared_brands: number
  shared_skus: number
  rival_brand_count: number // breadth of their own menu
  rival_sku_count: number
  tracked: boolean
  blocked: boolean
  previous?: {
    collected_at: string
    brand_overlap: number
    sku_overlap: number
    shared_brands: number
    shared_skus: number
  } | null
}

export interface RivalDensityData {
  location: { id: string; name: string }
  radius_mi: number
  own_brand_total: number // denominator for brand overlap
  own_sku_total: number // denominator for SKU overlap
  total_rivals: number // physical shops in range with menu data
  rivals: RivalDensityRow[]
}

export function useRivalDensity(
  locationId: string | null,
  radius: number,
  type?: string
) {
  const authFetch = useAuthFetch()
  const [data, setData] = useState<RivalDensityData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState(0)
  const refresh = useCallback(() => setRefreshToken((value) => value + 1), [])

  useEffect(() => {
    if (!locationId) return
    let alive = true
    setLoading(true)
    setError(null)
    const params = new URLSearchParams({ location_id: locationId, radius: String(radius) })
    if (type && type !== 'both') params.set('type', type)

    authFetch(`${API}/api/v1/rivals/density?${params}`)
      .then(async (r) => {
        const body = await r.json()
        if (!r.ok || body?.success === false) {
          throw new Error(body?.error || 'Couldn’t load rival density.')
        }
        return body
      })
      .then((body) => {
        if (!alive) return
        setData(body.data as RivalDensityData)
        setLoading(false)
      })
      .catch((err) => {
        if (!alive) return
        setError(err.message)
        setData(null)
        setLoading(false)
      })
    return () => { alive = false }
  }, [locationId, radius, type, authFetch, refreshToken])

  return { data, loading, error, refresh }
}
